import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Form, TooltipHelp } from '@weezevent/nacre';
import { Dropdown } from 'semantic-ui-react';

export function RateTemplateSeatingForm({ rateTemplate = null, charts = [], form = {}, categories = [], contingents = [], updateForm = () => {} }) {
    const { t } = useTranslation();
    const [chart, setChart] = React.useState(rateTemplate.chart);

    const chartOptions = React.useMemo(() => {
        return charts.map(chart => ({
            text: chart.name,
            value: chart.uuid,
            key: chart.uuid
        }));
    }, [charts]);

    const filterByChartMapToOptions = React.useCallback(
        array => {
            if (!chart) {
                return [];
            }

            return array
                .filter(item => item.chart === chart)
                .map(item => ({
                    text: item.name,
                    value: item.pk,
                    key: item.pk
                }));
        },
        [chart]
    );

    const contingentOptions = React.useMemo(() => {
        return filterByChartMapToOptions(contingents);
    }, [contingents, filterByChartMapToOptions]);

    const categoriesOptions = React.useMemo(() => {
        return filterByChartMapToOptions(categories);
    }, [categories, filterByChartMapToOptions]);

    const onChangeCategory = React.useCallback(
        value => {
            if (value && categories.find(cat => cat.pk === value)?.general_admission) {
                updateForm('contingents', []);
            }
            updateForm('category', value);
        },
        [updateForm, categories]
    );

    if (!charts.length) {
        return null;
    }

    return (
        <>
            <Form>
                <Form.Field>
                    <Form.Label>{t(`ticket.chart.chart`)}</Form.Label>
                    <Dropdown
                        disabled={rateTemplate?.events?.length}
                        fluid
                        search
                        selection
                        options={chartOptions}
                        onChange={(synth, data) => setChart(data.value)}
                        value={chart}
                    />
                </Form.Field>
                <Form.Field>
                    <Form.Label>
                        {t(`ticket.rates.category`)}
                        <TooltipHelp title={t('ticket.rates.categoryInformation')} />
                    </Form.Label>
                    <Dropdown
                        disabled={!chart}
                        clearable
                        fluid
                        search
                        selection
                        options={categoriesOptions}
                        onChange={(synth, data) => onChangeCategory(data.value)}
                        value={form.category}
                    />
                </Form.Field>
                <Form.Field>
                    <Form.Label>{t('ticket.rates.contingents')}</Form.Label>
                    <Dropdown
                        disabled={!chart || (form.category && categories.find(({ pk }) => pk === form.category)?.general_admission)}
                        clearable
                        fluid
                        search
                        selection
                        options={contingentOptions}
                        onChange={(synth, data) => updateForm('contingents', data.value)}
                        value={form.contingents || []}
                        multiple
                    />
                </Form.Field>
                <Form.Field>
                    <Form.Label>
                        {t('ticket.rates.inventory')}
                        <TooltipHelp title={t('ticket.rates.inventoryInformationTemplate')} />
                    </Form.Label>
                    <Dropdown
                        disabled
                        clearable
                        fluid
                        search
                        selection
                        options={[
                            {
                                text: t('ticket.rates.inventoryDetail'),
                                value: 0,
                                key: 0
                            }
                        ]}
                        value={0}
                    />
                </Form.Field>
            </Form>
        </>
    );
}

RateTemplateSeatingForm.propTypes = {
    rateTemplate: PropTypes.object,
    charts: PropTypes.array,
    form: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    contingents: PropTypes.array.isRequired,
    updateForm: PropTypes.func.isRequired
};
